import { useEffect, useRef } from "react";

/**
 * True when a `mousedown` originated on the page's native scrollbar rather
 * than on a real element.
 *
 * The document scrolls on the window here (nothing in the app sets `overflow`
 * on html/body/#root), so clicking the native vertical scrollbar dispatches a
 * `mousedown` whose `target` is the root `<html>` element. No real control — a
 * date cell, the time field, a button — is ever the `<html>` element, so
 * keying off the *target* (not cursor coordinates) means an ordinary click is
 * never misclassified: panels still dismiss when you click the time field or
 * anywhere else outside them. This suppresses only the page-scrollbar drag
 * that previously closed the calendar mid-scroll (1NOW-1097).
 *
 * NOTE: an earlier attempt compared `clientX/Y` against
 * `documentElement.clientWidth/Height`; that misfired on normal clicks and
 * stopped panels from closing at all, so detection is deliberately
 * target-based now.
 */
export const isPageScrollbarClick = (event: MouseEvent): boolean =>
  event.target === document.documentElement;

/**
 * Closes the panel when the user clicks outside the referenced element.
 *
 * Implementation note: react-datepicker (and a few other inner pickers used
 * by the search bar) re-renders synchronously when a date or option is
 * clicked, which detaches the clicked node from the DOM before the document
 * `click` listener fires. That made the old `ref.current.contains(target)`
 * check report "outside" for clicks that were actually inside the panel —
 * so the panel closed without committing the selection.
 *
 * The fix:
 *   1. Listen on `mousedown` — fires before React's synchronous re-render,
 *      so the target is still attached.
 *   2. Use `event.composedPath()` — captured at dispatch time and survives
 *      later DOM mutations, in case any handler still detaches the node.
 *   3. Fall back to `contains()` on browsers without composedPath.
 *
 * Happy-path behaviour is unchanged: outside clicks still close the panel,
 * inside clicks still don't. Only the previously-broken case (a descendant
 * clicked just before being unmounted) is now correctly classified.
 */
export const useClickOutside = (
  ref: React.RefObject<HTMLElement>,
  isOpen: boolean,
  onClose: () => void
) => {
  const enabledRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      enabledRef.current = false;
      return;
    }

    // Delay enabling the handler until AFTER the event loop tick so the
    // opening click itself doesn't trigger a close.
    const t = setTimeout(() => {
      enabledRef.current = true;
    }, 0);

    // True when the event happened outside the panel. Uses composedPath()
    // (captured at dispatch) so a descendant that react-datepicker detaches
    // mid-handler is still recognised as "inside".
    const isOutside = (event: MouseEvent) => {
      const node = ref.current;
      if (!node) return false;

      const path =
        typeof (event as any).composedPath === "function"
          ? ((event as any).composedPath() as EventTarget[])
          : null;

      if (path && path.includes(node)) return false;
      if (!path && node.contains(event.target as Node)) return false;

      return true;
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (!enabledRef.current) return;
      // Dragging the page's native scrollbar fires mousedown on <html>; that
      // isn't an outside click, so keep the panel open (1NOW-1097).
      if (isPageScrollbarClick(event)) return;
      if (isOutside(event)) onClose();
    };

    // Sibling Headless UI popovers — e.g. the time field in
    // RentalCarTimesRangeInput — dismiss their peers with a synthetic
    // `document.querySelector("html").click()`, and they stop the real
    // mousedown from propagating. A mousedown-only listener therefore never
    // learns the user opened another control, so this panel would stay open
    // (calendar not closing when the time dropdown opens). Listening for the
    // click too closes it. No scrollbar guard here: the native scrollbar never
    // fires a click, and the html.click() signal legitimately means "close".
    const handleClick = (event: MouseEvent) => {
      if (!enabledRef.current) return;
      if (isOutside(event)) onClose();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("click", handleClick);

    return () => {
      enabledRef.current = false;
      clearTimeout(t);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("click", handleClick);
    };
  }, [isOpen, ref, onClose]);
};
